import { Box } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ReactComponent as IconCalPrev } from '../../assets/icons/caret_left.svg';
// import { ReactComponent as IconTick } from '../../assets/icons/check_mark.svg';
import { ReactComponent as IconTick } from '../../assets/icons/check_small.svg';
import { IconButton } from '../../components/IconButton';
import { Spacer } from '../../components/Spacer';
import { Typography } from '../../components/Typography';
import { updateBookingStepAction } from '../../data/actions/bookingAction';
import { freeBookingAction } from '../../data/actions/obeeBookingAction';
import { setRestaurantAction } from '../../data/actions/restaurantAction';
import { bookingPropTypes, bookingSteps, isEditing } from '../../data/models/Booking';
import { addonGroupsPropTypes } from '../../data/models/Experience';
import { restaurantPropTypes } from '../../data/models/Restaurant';
import { getAddonGroupsForBooking } from '../../utils/bookingHelpers';
import { formatDateWithYear, isEmpty, minutesToTimeString, shadeHex } from '../../utils/helpers';
import { useIsMobile } from '../../utils/hooks';
import useStyles from './WidgetLayoutStyles';

const WidgetLayout = ({
  booking,
  children,
  restaurant,
  setRestaurant,
  showDetails,
  showBackButton,
  size,
  updateBookingStep,
  addonGroups,
  freeBooking,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const classes = useStyles({
    size,
  });

  const { waitlist } = booking;

  const onBack = () => {
    const experienceHasTermsBeforeAddons = booking?.selectedExperiences?.some(
      (experience) => experience?.showTermsSeparately && !isEmpty(experience?.termsAndConditions),
    );

    const loadAvailability = () => {
      if (!isEditing(booking) && booking.heldBookingId) {
        freeBooking(booking.heldBookingId);
      }

      updateBookingStep(bookingSteps.availability);
    };

    // Screens shown can be slightly different if editing, using addons, etc.
    switch (booking.step) {
      case bookingSteps.details:
        // Waitlist bookings should ignore addons and just go straight back to the start
        if (booking?.waitlist) {
          loadAvailability();
          return;
        }

        // If there were addons selected before the details screen
        const addonGroupsBeforeDetails = getAddonGroupsForBooking(
          booking,
          addonGroups,
        )?.beforeDetails;

        if (!isEmpty(addonGroupsBeforeDetails)) {
          const addonGroupsLength = addonGroupsBeforeDetails.length;
          updateBookingStep(bookingSteps.addonsBeforeDetails, addonGroupsLength - 1);
          break;
        }

        // If showing terms, go back to that screen
        if (experienceHasTermsBeforeAddons) {
          updateBookingStep(bookingSteps.terms);
          return;
        }

        if (booking.initialRestId !== restaurant.data.objectId && !isEditing(booking)) {
          // set the parent venue onBack if we aren't in the edit flow for a different venue
          setRestaurant(booking.initialRestId);
          break;
        }

        loadAvailability();

        break;
      case bookingSteps.review:
        // Go to addons if there are any
        // TODO do filtering for addon days, dates, group sizes, etc.
        const addonGroupsAfterDetails = getAddonGroupsForBooking(
          booking,
          addonGroups,
        )?.afterDetails;

        if (!isEmpty(addonGroupsAfterDetails)) {
          const addonGroupsLength = addonGroupsAfterDetails.length;
          updateBookingStep(bookingSteps.addons, addonGroupsLength - 1);
          break;
        }

        updateBookingStep(bookingSteps.details);
        break;
      case bookingSteps.addons:
        // Waitlist bookings should ignore addons and just go straight back to the start
        if (booking?.waitlist) {
          loadAvailability();
          return;
        }

        // If there are other addons before this, go back just one step
        if (booking?.subStep > 0) {
          updateBookingStep(bookingSteps.addons, booking?.subStep - 1);
          break;
        }

        updateBookingStep(bookingSteps.details);
        break;
      case bookingSteps.addonsBeforeDetails:
        // If there are other addons before this, go back just one step
        if (booking?.subStep > 0) {
          updateBookingStep(bookingSteps.addonsBeforeDetails, booking?.subStep - 1);
          break;
        }

        // If showing terms, go back to that screen
        if (experienceHasTermsBeforeAddons) {
          updateBookingStep(bookingSteps.terms);
          return;
        }

        loadAvailability();
        break;
      case bookingSteps.terms:
        // Same logic as going back from details screen when no addons
        if (booking.initialRestId !== restaurant.data.objectId && !isEditing(booking)) {
          // set the parent venue onBack if we aren't in the edit flow for a different venue
          setRestaurant(booking.initialRestId);
          break;
        }

        loadAvailability();
        break;
      case bookingSteps.availability:
      case bookingSteps.confirm:
      default:
    }
  };

  const selectedAreaId = booking?.availabilitySuggestion?.table?.areaId || booking?.areaId;
  const selectedArea =
    booking?.availabilitySuggestion?.table?.areaLabel ??
    restaurant.data.areas.find((area) => area?.id === selectedAreaId)?.name;

  const selectedExperience = (booking?.selectedExperiences ?? [])
    .filter((experienceCount) => experienceCount?.count > 0)
    .map((experience) => experience?.experience?.name)
    .join(', ');

  return (
    <Box
      style={{
        background: restaurant.data.availabilityBackgroundBoxBgColour,
        border: `${restaurant.data.availabilityBackgroundBoxBorderWeight}px solid ${restaurant.data.availabilityBackgroundBoxBorderColour}`,
        fontFamily: `${restaurant.data.availabilityFontFamilies}, 'Open Sans', 'Helvetica', 'sans-serif'`,
        margin: '12px',
      }}
    >
      {showDetails && (
        <Spacer
          gap={12}
          className={classes.bookingsDetails}
          style={{
            alignItems: 'stretch',
            borderBottom: `1px solid ${
              restaurant.data.formHeaderUnderlineColor ?? theme.colors.outlineColour
            }`,
          }}
        >
          {showBackButton && (
            <>
              <IconButton
                onClick={onBack}
                style={{
                  // color: isBright(restaurant.data.widgetHeaderBgColor)
                  //   ? theme.colors.charcoal
                  //   : theme.colors.white,
                  height: '28px',
                  width: '28px',
                }}
                variant='outlined'
              >
                <IconCalPrev width={8} />
              </IconButton>

              {!isMobile && (
                <Box
                  style={{
                    borderRight: `1px solid ${shadeHex(
                      theme.colors.availabilityBackgroundBoxBgColour,
                      -8.4,
                    )}`,
                  }}
                />
              )}
            </>
          )}

          <Spacer
            direction={isMobile ? 'vertical' : 'horizontal'}
            style={{
              color: theme.colors.mainTextColour,
              display: isMobile ? 'flex' : 'grid',
              // gridTemplateColumns: '1fr auto',
              gridTemplateColumns: 'minmax(150px, 1fr) minmax(50%, auto)',
              rowGap: '16px',
              padding: '4px',
              columnGap: '16px',
              width: '100%',
            }}
          >
            {!isEmpty(selectedExperience) && (
              <Spacer style={{ justifyContent: 'flex-start', gap: '12px' }}>
                <IconTick style={{ marginTop: '2px', minWidth: '15px' }} />
                <Typography>{selectedExperience}</Typography>
              </Spacer>
            )}

            {[
              formatDateWithYear(booking.date, 'ddd Do of MMMM'),
              `${booking.guests} ${booking.guests > 1 ? 'guests' : 'guest'}`,
              `${
                waitlist ? 'Requested time' : restaurant?.data?.arrivalTimeLabel
              } ${minutesToTimeString(booking.availabilitySuggestion?.time)}`,
            ].map((item) => (
              <Spacer
                centered={!isMobile}
                key={item}
                style={{
                  justifyContent: 'flex-start',
                  gap: '12px',
                }}
              >
                <IconTick style={{ marginTop: '2px', minWidth: '15px' }} />
                <Typography>{item}</Typography>
              </Spacer>
            ))}

            {isEmpty(selectedExperience) && !isEmpty(selectedArea) && (
              <Spacer style={{ justifyContent: 'flex-start', gap: '12px' }}>
                <IconTick style={{ marginTop: '2px', minWidth: '15px' }} />
                <Typography>{selectedArea}</Typography>
              </Spacer>
            )}
          </Spacer>
        </Spacer>
      )}

      <Box className={classes.widget}>{children}</Box>
    </Box>
  );
};

WidgetLayout.defaultProps = {
  showDetails: false,
  showBackButton: true,
  size: 'regular',
};

WidgetLayout.propTypes = {
  booking: bookingPropTypes.isRequired,
  children: PropTypes.node.isRequired,
  restaurant: restaurantPropTypes.isRequired,
  setRestaurant: PropTypes.func.isRequired,
  showDetails: PropTypes.bool,
  showBackButton: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'regular']),
  updateBookingStep: PropTypes.func.isRequired,
  addonGroups: addonGroupsPropTypes.isRequired,
  freeBooking: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
  addonGroups: state.addonGroups,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      setRestaurant: setRestaurantAction,
      updateBookingStep: updateBookingStepAction,
      freeBooking: freeBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(WidgetLayout);
